import axios from 'axios';
import jsCookies from 'js-cookie';
import { PHS_TOKEN_COOKIE, USER_TOKEN_COOKIE } from '../../../app/settings';
import { API_BASE_URL, API_KEY, ROOT_DOMAIN } from '@env';
import { EUserTokenType, TGetUserTokenFunction } from './getUserToken.types';
import { logError } from 'src/_shared/logger';
import { GLOBAL_ERRORS } from '@photosi/rn-components/global-error-boundary/GlobalErrorBoundary.constants';

const MAX_ATTEMPTS_TO_REFRESH_PHS_TOKEN = 3;
const WAIT_TIME_MS_BETWEEN_REFRESH_ATTEMPTS = 500;

const getPhsToken = async () => {
  let token = jsCookies.get(PHS_TOKEN_COOKIE);
  if (!token) {
    try {
      token = await refreshPhsToken();
    } catch (error) {
      logError('[refreshPhsToken] Failed to refresh PHS token: ', error);

      // show modal of new version available
      throw new Error(GLOBAL_ERRORS.NewVersionAvailableError);
    }
  }
  return token;
};

const refreshPhsToken = async (attempt = 1): Promise<string> => {
  return axios({
    method: 'post',
    url: `${API_BASE_URL}accounts/guest-users`,
    headers: {
      'x-api-key': API_KEY,
      'Accept-Language': 'it',
    },
  })
    .then(({ data: { token } }) => {
      jsCookies.set(PHS_TOKEN_COOKIE, token, { domain: ROOT_DOMAIN, expires: 6 });
      return token;
    })
    .catch(error => {
      return new Promise<string>((resolve, reject) => {
        setTimeout(() => {
          if (attempt <= MAX_ATTEMPTS_TO_REFRESH_PHS_TOKEN) {
            resolve(refreshPhsToken(attempt + 1));
          } else {
            reject(error);
          }
        }, attempt * WAIT_TIME_MS_BETWEEN_REFRESH_ATTEMPTS);
      });
    });
};

export const getUserTokenType = (): EUserTokenType => {
  let isUser = jsCookies.get(USER_TOKEN_COOKIE);
  return isUser ? EUserTokenType.PHOTOSI : EUserTokenType.GUEST;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4 !== 0) {
      payload += '=';
    }
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

export const isUserTokenExpired = async (
  safetyWindowSecs: number = 12 * 3600, // 12h
): Promise<{ isExpired: boolean; tokenType: EUserTokenType }> => {
  const { userToken, tokenType } = await getUserToken();

  if (!userToken) {
    return { isExpired: true, tokenType };
  }

  const claims = decodeJwtPayload(userToken);
  if (!claims || typeof claims.exp !== 'number') {
    return { isExpired: true, tokenType };
  }

  const now = Date.now() / 1000;
  return { isExpired: claims.exp <= now + safetyWindowSecs, tokenType };
};

export const getUserToken: TGetUserTokenFunction = async () => {
  let isUserAuthenticated = false;
  let tokenType = getUserTokenType();
  let token = undefined;
  if (tokenType === EUserTokenType.PHOTOSI) {
    token = jsCookies.get(USER_TOKEN_COOKIE);
    isUserAuthenticated = true;
  } else {
    token = await getPhsToken();
    isUserAuthenticated = false;
  }

  return {
    userToken: token || '',
    tokenType,
    isUserAuthenticated,
  };
};

export const refreshUserToken: TGetUserTokenFunction = async () => {
  let isUserAuthenticated = false;
  let tokenType = getUserTokenType();
  let token = undefined;
  if (tokenType === EUserTokenType.GUEST) {
    token = await refreshPhsToken();
    isUserAuthenticated = false;
  } else if (tokenType === EUserTokenType.PHOTOSI) {
    // TODO: refresh del token photosì
    token = jsCookies.get(USER_TOKEN_COOKIE);
    isUserAuthenticated = true;
  }

  return {
    userToken: token || '',
    tokenType,
    isUserAuthenticated,
  };
};
